import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, Sparkles, Users, MessageSquare, FileText, Calendar,
  Compass, CheckCircle, Star, ArrowRight, Quote,
  BookMarked, Brain, Heart, Zap, Clock, Shield, ChevronRight,
  GraduationCap, Mic, Leaf, LogIn
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.png';

export default function LandingPage() {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  
  // Pricing data with 15% annual discount
  const pricing = {
    pro: { monthly: 17, yearly: 173.40, yearlyFull: 204, yearlySavings: 30.60 },
    studio: { monthly: 34, yearly: 346.80, yearlyFull: 408, yearlySavings: 61.20 },
    church_teams: { monthly: 69, yearly: 703.80, yearlyFull: 828, yearlySavings: 124.20 }
  };
  
  useEffect(() => {
    // Smart redirect logic
    const sessionToken = localStorage.getItem('session_token');
    const hasAccount = localStorage.getItem('has_account');
    
    // If user is logged in, go to dashboard
    if (sessionToken) {
      navigate('/dashboard');
      return;
    }
    
    // If user has signed up before on this device, go to login
    if (hasAccount === 'true') {
      navigate('/login');
      return;
    }
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src={logo} alt="PulpitPilot AI Logo" className="h-10 sm:h-12 w-auto" />
            <span className="font-bold text-xl sm:text-2xl text-gray-900">PulpitPilot AI</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              className="hidden sm:inline-flex text-gray-700 hover:text-gray-900"
            >
              Pricing
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="text-gray-700 hover:text-gray-900 border-gray-300"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Login
            </Button>
            <Button
              onClick={() => navigate('/login?signup=true')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full font-semibold shadow-lg"
            >
              Start Free
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-amber-100 text-amber-800 px-4 py-1.5 text-sm font-medium mb-6">
            🚀 Launch Week – Start Free Today
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Preach with clarity.
            <br />
            <span className="text-blue-600">Study with confidence.</span>
            <br />
            Sound like <span className="text-amber-500">YOU</span>.
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            PulpitPilot AI is an all-in-one preaching and Bible study platform designed to help pastors, 
            ministers, and preachers prepare sermons, study Scripture deeply, and plan what's next — 
            without burnout, guesswork, or generic AI output.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Button
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
              data-testid="hero-start-free-btn"
            >
              Join Launch Week – Start Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          <p className="text-sm text-gray-500 italic">
            *Navigating Your Next Great Sermon*
          </p>
        </div>
      </section>

      {/* Trust & Authority Strip */}
      <section className="py-10 px-4 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-amber-400 font-medium mb-2">Designed by</p>
          <h3 className="text-2xl sm:text-3xl font-bold mb-2">Pastor Kingsford Bloomfield</h3>
          <p className="text-gray-400 mb-6">Pastor • Preacher • Teacher • 10+ Years in Ministry</p>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-gray-800 px-4 py-2 rounded-full">✓ Pastors & Preachers</span>
            <span className="bg-gray-800 px-4 py-2 rounded-full">✓ Church Planters</span>
            <span className="bg-gray-800 px-4 py-2 rounded-full">✓ Teaching Teams</span>
            <span className="bg-gray-800 px-4 py-2 rounded-full">✓ New & Seasoned Ministers</span>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Preachers are under pressure — <span className="text-red-500">every single week.</span>
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-6">
                <Quote className="w-8 h-8 text-gray-400 mb-3" />
                <p className="text-gray-700 italic">"What am I preaching next?"</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-6">
                <Quote className="w-8 h-8 text-gray-400 mb-3" />
                <p className="text-gray-700 italic">"How do I study Scripture correctly?"</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-6">
                <Quote className="w-8 h-8 text-gray-400 mb-3" />
                <p className="text-gray-700 italic">"Why does AI sound nothing like me?"</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200 bg-gray-50">
              <CardContent className="p-6">
                <Quote className="w-8 h-8 text-gray-400 mb-3" />
                <p className="text-gray-700 italic">"Why do I need 5 different apps just to prepare one sermon?"</p>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center">
            <p className="text-lg text-gray-600 mb-4">Most tools solve <strong>one task</strong>.</p>
            <p className="text-2xl font-bold text-blue-600">PulpitPilot AI solves the preaching workflow.</p>
          </div>
        </div>
      </section>

      {/* What PulpitPilot Is */}
      <section className="py-16 px-4 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Not just a sermon generator — a <span className="text-amber-300">preaching companion</span>.
          </h2>
          <p className="text-xl text-blue-100">
            PulpitPilot AI brings sermon prep, Bible study, personalization, and planning 
            into <strong>one unified experience</strong>.
          </p>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-16 sm:py-24 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-green-100 text-green-800 px-4 py-1.5 text-sm font-medium mb-4">
              ✅ Live at Launch
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Core Features
            </h2>
          </div>
          
          <div className="space-y-12">
            {/* Feature 1: NextStep Navigator */}
            <Card className="overflow-hidden border-2 border-blue-200 bg-white">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center">
                      <Compass className="w-10 h-10 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      🧭 AI Sermon Preparation + NextStep Navigator™
                    </h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Generate sermons & sermon series instantly</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Tap <strong>"What's Next?"</strong> to plan your next message</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Chronological, topical, theological, and seasonal flows</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Build full sermon calendars with clarity</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature 2: Built-In Bible */}
            <Card className="overflow-hidden border-2 border-amber-200 bg-white">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center">
                      <BookOpen className="w-10 h-10 text-amber-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-bold text-gray-900">
                        📖 Built-In Bible
                      </h3>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">All Plans</Badge>
                    </div>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>All <strong>66 books</strong> of the Bible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Multiple English translations (KJV, NIV, ESV, NASB)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Cross-references & concordance tools</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span><strong>Book-by-book overviews</strong> at the start of every book</span>
                      </li>
                    </ul>
                    <p className="mt-4 text-blue-600 font-medium">One app. One Bible. Zero switching.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature 3: Study Guide */}
            <Card className="overflow-hidden border-2 border-purple-200 bg-white">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-purple-100 rounded-2xl flex items-center justify-center">
                      <BookMarked className="w-10 h-10 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      📚 3-2-3 Study Guide System
                    </h3>
                    <p className="text-gray-600 mb-4">Create your personal study desk:</p>
                    <div className="grid sm:grid-cols-3 gap-4 mb-4">
                      <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <span className="text-3xl font-bold text-purple-600">3</span>
                        <p className="text-sm text-gray-600">Bible translations</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <span className="text-3xl font-bold text-purple-600">2</span>
                        <p className="text-sm text-gray-600">Concordances</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <span className="text-3xl font-bold text-purple-600">3</span>
                        <p className="text-sm text-gray-600">Commentaries</p>
                      </div>
                    </div>
                    <p className="text-gray-600">
                      Automatically saves and resumes where you left off. Includes <strong>Bible Study Starter Tips</strong> 
                      so users learn <em>how</em> to study Scripture before diving in.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature 4: AI Study Companion */}
            <Card className="overflow-hidden border-2 border-green-200 bg-white">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center">
                      <MessageSquare className="w-10 h-10 text-green-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      🤖 PulpitPilot AI Study Companion
                    </h3>
                    <p className="text-gray-600 mb-4">Your always-available Bible guide:</p>
                    <ul className="space-y-2 text-gray-600 mb-4">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>3–5 minute overview of the entire Bible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Old & New Testament theological overview</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Quick refreshers inside the Study Guide & Bible</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>Book-level introductions as you study</span>
                      </li>
                    </ul>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-green-600">✔ New believers</Badge>
                      <Badge variant="outline" className="text-green-600">✔ New preachers</Badge>
                      <Badge variant="outline" className="text-green-600">✔ Seasoned leaders</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature 5: Digital Preaching Profile */}
            <Card className="overflow-hidden border-2 border-pink-200 bg-white">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-pink-100 rounded-2xl flex items-center justify-center">
                      <Brain className="w-10 h-10 text-pink-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      🧠 Digital Preaching Profile (Voice Personalization)
                    </h3>
                    <p className="text-gray-600 mb-4">
                      When you sign up, you'll answer <strong>20 deep, open-ended questions</strong> that 
                      build your personal <strong>Preaching DNA</strong>.
                    </p>
                    <div className="bg-pink-50 p-4 rounded-lg mb-4">
                      <p className="font-semibold text-gray-900 mb-2">The result:</p>
                      <ul className="space-y-1 text-gray-600">
                        <li>• Sermons that reflect your tone</li>
                        <li>• Language that sounds natural to you</li>
                        <li>• Structure that matches your flow</li>
                        <li>• AI that <em>learns</em> your voice over time</li>
                      </ul>
                    </div>
                    <p className="text-pink-600 font-bold text-lg">No generic sermons. Ever.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 sm:py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-800 px-4 py-1.5 text-sm font-medium mb-4">
            ✅ Live at Launch
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Built for collaboration, not comparison.
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900">Sermon Feedback Circles</h4>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <MessageSquare className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900">Study Discussions</h4>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6 text-center">
                <Heart className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                <h4 className="font-semibold text-gray-900">Prayer & Encouragement</h4>
              </CardContent>
            </Card>
          </div>
          
          <div className="mt-10 p-6 bg-gray-100 rounded-xl">
            <p className="text-gray-600">
              <strong>This is not social media.</strong><br />
              This is ministry support.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-amber-100 text-amber-800 px-4 py-1.5 text-sm font-medium mb-4">
              🚀 Honest Pricing
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Choose Your Plan
            </h2>
          </div>
          
          {/* Free Trial Banner */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 mb-8 max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold text-lg">Start with a 7-Day Free Trial</span>
                </div>
                <p className="text-blue-100 text-sm">
                  Add your card to start — you won&apos;t be charged until the trial ends.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-3 rounded-full"
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            {/* Trial Features */}
            <div className="mt-6 pt-6 border-t border-blue-500">
              <p className="text-sm text-blue-100 mb-3">What&apos;s included in your free trial:</p>
              <div className="flex flex-wrap justify-center gap-3 text-sm">
                <span className="bg-blue-500 px-3 py-1 rounded-full">✓ 3 Sermons</span>
                <span className="bg-blue-500 px-3 py-1 rounded-full">✓ 1 Sermon Series</span>
                <span className="bg-blue-500 px-3 py-1 rounded-full">✓ Full Bible Access</span>
                <span className="bg-blue-500 px-3 py-1 rounded-full">✓ Study Guide</span>
                <span className="bg-blue-500 px-3 py-1 rounded-full">✓ Basic Exports</span>
              </div>
            </div>
          </div>
          
          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center bg-white rounded-full p-1.5 shadow-lg border border-gray-200">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'yearly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                Annual
                <Badge className="bg-green-500 text-white text-xs font-bold px-2">15% OFF</Badge>
              </button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Pro */}
            <Card className="border-2 border-blue-500 relative shadow-xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-blue-600 text-white px-3 py-1">
                  <Star className="w-3 h-3 mr-1" /> Most Popular
                </Badge>
              </div>
              <CardContent className="p-6 pt-8">
                <div className="mb-4">
                  <span className="text-3xl">🔥</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">Pro</h3>
                  {billingPeriod === 'yearly' ? (
                    <div className="mt-2">
                      <p className="text-3xl font-bold text-gray-900">${pricing.pro.yearly.toFixed(2)}<span className="text-base font-normal text-gray-500">/year</span></p>
                      <p className="text-sm text-gray-400 line-through">${pricing.pro.yearlyFull}/year</p>
                      <p className="text-sm text-green-600 font-semibold">Save ${pricing.pro.yearlySavings.toFixed(2)}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-gray-900 mt-2">${pricing.pro.monthly}<span className="text-base font-normal text-gray-500">/mo</span></p>
                  )}
                </div>
                <ul className="space-y-3 mb-6 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Unlimited sermons & series
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    NextStep Navigator™
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Full study tools
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Personalized AI voice
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Get Pro
                </Button>
              </CardContent>
            </Card>

            {/* Studio */}
            <Card className="border-2 border-gray-200 relative">
              <CardContent className="p-6">
                <div className="mb-4">
                  <span className="text-3xl">🎙️</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">Studio</h3>
                  {billingPeriod === 'yearly' ? (
                    <div className="mt-2">
                      <p className="text-3xl font-bold text-gray-900">${pricing.studio.yearly.toFixed(2)}<span className="text-base font-normal text-gray-500">/year</span></p>
                      <p className="text-sm text-gray-400 line-through">${pricing.studio.yearlyFull}/year</p>
                      <p className="text-sm text-green-600 font-semibold">Save ${pricing.studio.yearlySavings.toFixed(2)}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-gray-900 mt-2">${pricing.studio.monthly}<span className="text-base font-normal text-gray-500">/mo</span></p>
                  )}
                </div>
                <ul className="space-y-3 mb-6 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Everything in Pro
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Video editing & clips
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    AI transcription
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline" 
                  className="w-full"
                >
                  Get Studio
                </Button>
              </CardContent>
            </Card>

            {/* Church Teams */}
            <Card className="border-2 border-gray-200 relative">
              <CardContent className="p-6">
                <div className="mb-4">
                  <span className="text-3xl">🏛️</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">Church Teams</h3>
                  {billingPeriod === 'yearly' ? (
                    <div className="mt-2">
                      <p className="text-3xl font-bold text-gray-900">${pricing.church_teams.yearly.toFixed(2)}<span className="text-base font-normal text-gray-500">/year</span></p>
                      <p className="text-sm text-gray-400 line-through">${pricing.church_teams.yearlyFull}/year</p>
                      <p className="text-sm text-green-600 font-semibold">Save ${pricing.church_teams.yearlySavings.toFixed(2)}</p>
                    </div>
                  ) : (
                    <p className="text-3xl font-bold text-gray-900 mt-2">${pricing.church_teams.monthly}<span className="text-base font-normal text-gray-500">/mo</span></p>
                  )}
                </div>
                <ul className="space-y-3 mb-6 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Up to 10 users
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Shared sermon vault
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Collaborative planning
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/login')}
                  variant="outline" 
                  className="w-full"
                >
                  Get Teams
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Coming in 2026 Section */}
      <section className="py-16 sm:py-24 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-amber-500 text-white px-4 py-1.5 text-sm font-medium mb-6">
            🔜 Coming in 2026
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            What's Next for PulpitPilot AI
          </h2>
          <p className="text-gray-400 text-lg mb-12">We're just getting started.</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Homiletics Mentor Pathway</h3>
              <p className="text-gray-400 text-sm">
                Discover your preaching style and grow with guided mentorship.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mic className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Preaching Style Expansion Studio</h3>
              <p className="text-gray-400 text-sm">
                Master expository, topical, narrative, and prophetic preaching.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Rest & Renewal Hub</h3>
              <p className="text-gray-400 text-sm">
                Support for pastors navigating burnout while staying faithful.
              </p>
            </div>
          </div>
          
          <p className="mt-12 text-gray-500 italic">
            Your subscription helps build what's next.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Launch Week Is Live
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Be part of the foundation of a platform built <strong>for preachers, by a preacher</strong>.
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-white text-blue-600 hover:bg-gray-100 px-10 py-6 text-lg rounded-full font-bold shadow-xl"
          >
            Start Free with PulpitPilot AI Today
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src={logo} alt="PulpitPilot AI Logo" className="h-10 w-auto brightness-0 invert" />
              <span className="font-bold text-xl">PulpitPilot AI</span>
            </div>
            <p className="text-gray-400 italic text-center md:text-right">
              Navigating Your Next Great Sermon
            </p>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm mb-4 md:mb-0">
              Rooted in Scripture • Built for the Church • Powered by Wisdom & Technology
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <a href="#" className="hover:text-white">Privacy</a>
              <span onClick={() => navigate('/terms')} className="hover:text-white cursor-pointer">Terms</span>
              <a href="#" className="hover:text-white">Support</a>
              <a href="#" className="hover:text-white">Contact</a>
            </div>
          </div>
          
          <p className="text-center text-gray-600 text-sm mt-8">
            © 2025 PulpitPilot AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
